import React, {
  useState, createContext, useContext, useMemo, useEffect
} from 'react';

import {
  DEFAULT_VIEW_MODE, NEARBY_GAUGES_RADIUS, VIEW_MODES,
  calculateHaversineDistance, computeBoundingBox
} from './utils';
import { parseAreasCSV, parseGaugesCSV } from './parseData';

const SessionContext = createContext();

export function SessionProvider({ children }) {
  const [dates, setDates] = useState();
  const [continuousSelectedDateIndex, setContinuousSelectedDateIndex] = useState();
  const [selectedAttribute, setSelectedAttribute] = useState('wli');
  const [areas, setAreas] = useState();
  const [shapes, setShapes] = useState();
  const [meta, setMeta] = useState();
  const [isLoading, setIsLoading] = useState(true);
  const [loadError, setLoadError] = useState();
  const [viewMode, setViewMode] = useState(DEFAULT_VIEW_MODE);
  const [hexagonOpacity, setHexagonOpacity] = useState(100);
  const [gaugePositions, setGaugePositions] = useState();
  const [nearbyGauges, setNearbyGauges] = useState();
  const [selectedLocation, setSelectedLocation] = useState();
  const [selectedArea, setSelectedArea] = useState();
  const [isMobile, setIsMobile] = useState(window.innerWidth);
  const [showDisclaimerModal, setShowDisclaimerModal] = useState(false);
  const [daysPerSecond, setDaysPerSecond] = useState(8);
  const lightInterface = useMemo(() => process.env.REACT_APP_INTERFACE === 'light', []);

  const discreteSelectedDateIndex = useMemo(() => (
    Math.round(continuousSelectedDateIndex)
  ), [continuousSelectedDateIndex]);

  /* Fetch values, and -- in administrative mode -- geometry, for the active view.
   *
   * The two are separate requests on purpose. Geometry changes when the boundary set or
   * the gauge fleet changes, which is rarely; values change every day. Serving them
   * together would re-download 1.5 MB of unchanged polygons with every daily refresh,
   * and would put the geometry behind whatever cache lifetime the values need.
   */
  useEffect(() => {
    let cancelled = false;
    const mode = VIEW_MODES[viewMode];

    const task = async () => {
      setIsLoading(true);
      setLoadError();
      try {
        const [valuesResponse, shapesResponse] = await Promise.all([
          fetch(mode.jsonURL),
          mode.shapesURL ? fetch(mode.shapesURL) : Promise.resolve(null)
        ]);
        if (!valuesResponse.ok) {
          throw new Error(`values ${valuesResponse.status} from ${mode.jsonURL}`);
        }
        if (shapesResponse && !shapesResponse.ok) {
          throw new Error(`shapes ${shapesResponse.status} from ${mode.shapesURL}`);
        }

        const { gauges, wli, meta: newMeta } = await valuesResponse.json();
        const [areaKeys, newDates, levelHistory, gaugeCounts] = await parseAreasCSV(wli);
        const newGaugePositions = await parseGaugesCSV(gauges);
        const maxLogGaugeCount = Math.log(gaugeCounts.reduce((a, b) => Math.max(a, b)));

        const newAreas = areaKeys.map((areaKey, areaIndex) => ({
          areaKey,
          // kept so the H3 layer's `getHexagon` accessor and the search code read the
          // same field name in both modes
          hexagonCode: areaKey,
          gaugeCount: gaugeCounts[areaIndex],
          gaugeCountNorm: Math.log(gaugeCounts[areaIndex]) / maxLogGaugeCount,
          timeline: newDates.map((_, dateIndex) => levelHistory[areaIndex][dateIndex])
        }));

        let newShapes;
        if (shapesResponse) {
          const collection = await shapesResponse.json();
          // Bounding boxes once, here, rather than per point-in-polygon test. Without
          // this a single geo-search walks every coordinate in the file.
          collection.features.forEach((feature) => {
            // eslint-disable-next-line no-param-reassign
            feature.bbox = computeBoundingBox(feature.geometry);
          });
          newShapes = collection;
        }

        if (cancelled) return;
        setDates(newDates);
        setGaugePositions(newGaugePositions);
        setContinuousSelectedDateIndex(newDates.length - 1);
        setAreas(newAreas);
        setShapes(newShapes);
        setMeta(newMeta);
        setIsLoading(false);
      } catch (error) {
        if (cancelled) return;
        // A failed fetch used to leave the map permanently on "Fetching data..." with the
        // reason only in the console. Switching views can fail now, so it has to surface.
        setLoadError(error.message);
        setIsLoading(false);
      }
    };
    task();
    return () => { cancelled = true; };
  }, [viewMode]);

  // when a location (from search) finishes loading, get its nearby gauges
  useEffect(() => {
    if (!gaugePositions || !selectedLocation) {
      setNearbyGauges();
      return;
    }

    const distances = gaugePositions.map((position) => ({
      ...position,
      distance: calculateHaversineDistance(
        selectedLocation.lat,
        selectedLocation.lng,
        position.lat,
        position.lng
      )
    }));

    distances.sort((a, b) => a.distance - b.distance);

    setNearbyGauges(distances.filter(({ distance }) => distance < NEARBY_GAUGES_RADIUS));
  }, [gaugePositions, selectedLocation]);

  useEffect(() => {
    function handleResize() {
      setIsMobile(window.innerWidth <= 600);
    }

    window.addEventListener('resize', handleResize);

    handleResize();

    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    if (!selectedLocation || !areas) {
      setSelectedArea();
    } else {
      // `areaKey` is the h3 cell in hexagon mode and the boundary id in administrative
      // mode; `LeftPanel` resolves it for whichever view is active.
      setSelectedArea(areas.find((area) => area.areaKey === selectedLocation.areaKey));
    }
  }, [selectedLocation, areas]);

  const clearSelectedLocation = () => {
    setSelectedLocation();
    setNearbyGauges();
  };

  const value = useMemo(() => ({
    areas,
    shapes,
    meta,
    isLoading,
    loadError,
    viewMode,
    setViewMode,
    dates,
    selectedLocation,
    setSelectedLocation,
    nearbyGauges,
    selectedAttribute,
    setSelectedAttribute,
    hexagonOpacity,
    setHexagonOpacity,
    clearSelectedLocation,
    selectedArea,
    isMobile,
    showDisclaimerModal,
    setShowDisclaimerModal,
    daysPerSecond,
    setDaysPerSecond,
    continuousSelectedDateIndex,
    setContinuousSelectedDateIndex,
    discreteSelectedDateIndex,
    lightInterface
  }), [
    areas, shapes, meta, isLoading, loadError, viewMode, dates, selectedLocation,
    setSelectedLocation, nearbyGauges, selectedAttribute, setSelectedAttribute,
    hexagonOpacity, setHexagonOpacity, clearSelectedLocation, selectedArea,
    isMobile, showDisclaimerModal, daysPerSecond, continuousSelectedDateIndex,
    discreteSelectedDateIndex
  ]);

  return <SessionContext.Provider value={value}>{children}</SessionContext.Provider>;
}

export function useSessionContext() {
  return useContext(SessionContext);
}
